import { getApiBaseUrl as getPermanentApiBaseUrl } from "../config/permanent-urls";
import {
  getJobSearchSourceIdsForDomains,
  hydrateJobSearchFilters,
  type JobSearchFilters,
} from "../features/jobSearch/config";

const base = () => getPermanentApiBaseUrl();
const agentsCollectionPath = "/job-search-agents/";
const discoveredJobsCollectionPath = "/discovered-jobs/";

export interface JobSearchAgent {
  agent_id: string;
  name: string;
  filters: JobSearchFilters;
  source_ids: string[];
  sites: string[];
  query: string;
  context: string;
  interval_weeks: number;
  is_active: boolean;
  created_at?: number;
  updated_at?: number;
  next_run_at?: number | null;
  last_run_at?: number | null;
  last_run_status?: string | null;
}

export interface JobSearchAgentRun {
  run_id: string;
  agent_id: string;
  trigger: "scheduled" | "manual";
  status:
    | "queued"
    | "processing"
    | "completed"
    | "completed_with_errors"
    | "failed"
    | "skipped_empty";
  queued_at?: number | null;
  started_at?: number | null;
  completed_at?: number | null;
  new_jobs_count: number;
  duplicate_skipped_count: number;
  error_message?: string;
  site_summaries: Array<{
    domain: string;
    newCount?: number;
    duplicateCount?: number;
    resultCount?: number;
    error?: string;
  }>;
}

export interface DiscoveredJob {
  discovered_id: string;
  agent_id: string;
  canonical_url: string;
  title: string;
  company: string;
  location: string;
  source: string;
  snippet: string;
  full_content?: string;
  discovered_at: number;
}

export interface JobSearchAgentEstimate {
  tokensPerRun: number;
  sourceCount: number;
  tokenCostPerSearch: number;
}

export interface JobSearchAgentMutation {
  name: string;
  filters: JobSearchFilters;
  sourceIds: string[];
  intervalWeeks: number;
  isActive: boolean;
}

const normalizeString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
};

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const values: string[] = [];
  const seen = new Set<string>();
  value.forEach((item) => {
    const normalized = normalizeString(item);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    values.push(normalized);
  });
  return values;
};

const normalizeNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const hydrateAgent = (value: unknown): JobSearchAgent => {
  const record =
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const sites = normalizeStringList(record.sites);
  const explicitSourceIds = normalizeStringList(
    record.source_ids ?? record.sourceIds,
  );
  const sourceIds =
    explicitSourceIds.length > 0
      ? explicitSourceIds
      : getJobSearchSourceIdsForDomains(sites);

  return {
    agent_id: normalizeString(record.agent_id ?? record.agentId),
    name: normalizeString(record.name),
    filters: hydrateJobSearchFilters(record.filters),
    source_ids: sourceIds,
    sites,
    query: normalizeString(record.query),
    context: normalizeString(record.context),
    interval_weeks:
      normalizeNumber(record.interval_weeks ?? record.intervalWeeks) ?? 1,
    is_active:
      typeof record.is_active === "boolean"
        ? record.is_active
        : typeof record.isActive === "boolean"
          ? record.isActive
          : true,
    created_at: normalizeNumber(record.created_at ?? record.createdAt),
    updated_at: normalizeNumber(record.updated_at ?? record.updatedAt),
    next_run_at: normalizeNumber(record.next_run_at ?? record.nextRunAt) ?? null,
    last_run_at: normalizeNumber(record.last_run_at ?? record.lastRunAt) ?? null,
    last_run_status:
      normalizeString(record.last_run_status ?? record.lastRunStatus) || null,
  };
};

function headers(): Headers {
  const h = new Headers();
  h.set("Content-Type", "application/json");
  const token = localStorage.getItem("token");
  if (token) {
    h.set("Authorization", `Bearer ${token}`);
  }
  return h;
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem("token");
  if (token) {
    const { tokenRefreshService } = await import("./tokenRefreshService");
    if (tokenRefreshService.isTokenExpiredOrExpiringSoon(token)) {
      await tokenRefreshService.forceRefresh().catch(() => {});
    }
  }
  return fetch(url, { ...init, headers: headers(), credentials: "omit" });
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: T = {} as T;
  if (text) {
    try {
      data = JSON.parse(text) as T;
    } catch {
      throw new Error("Invalid JSON response");
    }
  }
  if (!res.ok) {
    const err =
      (data as { error?: string; message?: string }).message ||
      (data as { error?: string }).error ||
      res.statusText;
    throw new Error(err);
  }
  return data;
}

export async function listAgents(): Promise<JobSearchAgent[]> {
  const res = await authFetch(`${base()}${agentsCollectionPath}`);
  const body = await parseJson<{
    success?: boolean;
    data?: { agents?: JobSearchAgent[] };
  }>(res);
  return (body.data?.agents || []).map(hydrateAgent);
}

export async function createAgent(
  payload: JobSearchAgentMutation,
): Promise<JobSearchAgent> {
  const res = await authFetch(`${base()}${agentsCollectionPath}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const body = await parseJson<{ data?: JobSearchAgent }>(res);
  if (!body.data) {
    throw new Error("Invalid response");
  }
  return hydrateAgent(body.data);
}

export async function updateAgent(
  agentId: string,
  patch: Partial<JobSearchAgentMutation>,
): Promise<JobSearchAgent> {
  const res = await authFetch(`${base()}/job-search-agents/${agentId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  const body = await parseJson<{ data?: JobSearchAgent }>(res);
  if (!body.data) {
    throw new Error("Invalid response");
  }
  return hydrateAgent(body.data);
}

export async function deleteAgent(agentId: string): Promise<void> {
  const res = await authFetch(`${base()}/job-search-agents/${agentId}`, {
    method: "DELETE",
  });
  await parseJson(res);
}

export async function runAgent(agentId: string): Promise<JobSearchAgentRun> {
  const res = await authFetch(`${base()}/job-search-agents/${agentId}/runs`, {
    method: "POST",
  });
  const body = await parseJson<{ data?: { run?: JobSearchAgentRun } }>(res);
  if (!body.data?.run) {
    throw new Error("Invalid run response");
  }
  return body.data.run;
}

export async function listRuns(agentId: string): Promise<JobSearchAgentRun[]> {
  const res = await authFetch(`${base()}/job-search-agents/${agentId}/runs`);
  const body = await parseJson<{ data?: { runs?: JobSearchAgentRun[] } }>(res);
  return body.data?.runs || [];
}

export async function listDiscoveredJobs(
  agentId?: string,
): Promise<DiscoveredJob[]> {
  const suffix = agentId
    ? `${discoveredJobsCollectionPath}?agentId=${encodeURIComponent(agentId)}`
    : discoveredJobsCollectionPath;
  const res = await authFetch(`${base()}${suffix}`);
  const body = await parseJson<{ data?: { items?: DiscoveredJob[] } }>(res);
  return body.data?.items || [];
}

export async function estimateTokens(payload: {
  sourceIds?: string[];
  sites?: string[];
}): Promise<JobSearchAgentEstimate> {
  const res = await authFetch(`${base()}/job-search-agents/estimate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const body = await parseJson<{ data?: JobSearchAgentEstimate }>(res);
  if (!body.data) {
    throw new Error("Invalid estimate response");
  }
  return body.data;
}
